import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { trashGalleryIcon } from "../../Components";
import { plusIcon, Modal } from "../../Components";
import { GalleryVideo } from "../../Components";
import { galleryService, uploadService } from "../../Services";

export default function GalleryAdd({
  setShowGalleryUpload,
  briefImage,
  setShowConfirmInfModal,
  newGalleryUploaded,
  setNewGalleryUploaded,
  setTab,
  setToastList,
  toastList,
  close,
  uploadOnServer = true,
}) {
  const createAsset = useRef(null);
  const fileRef = useRef(null);
  const [error, setError] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [disableCreateButton, setDisableCreateButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFileUpload, setShowFileUpload] = useState(true);
  const [showActionIcon, setShowActionIcon] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const previewImage = () => {
    setErrorMessage("");
    if (fileRef.current.files && fileRef.current.files.length > 0) {
      const files = Array.from(fileRef.current.files);

      // Validate all files first
      const invalidFiles = files.filter((file) => {
        const fileType = file.type;
        return (
          (!fileType.startsWith("image") && !fileType.startsWith("video")) ||
          file.size > 10000000
        );
      });

      if (invalidFiles.length > 0) {
        addToast({
          error: true,
          date: new Date(),
          msg: ".GIF, .PNG, .JPG, .MP4, .MOV only with a maximum file size of 10MB",
        });
        return;
      }

      // Upload all valid files
      setDisableCreateButton(true);
      setUploadingCount(files.length);

      files.forEach((file, index) => {
        uploadFile(file, index);
      });
    }
  };

  const decodeBase64Json = (encodedString) => {
    const decodedString = atob(encodedString);
    try {
      const jsonObject = JSON.parse(decodedString);
      return jsonObject;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null;
    }
  };

  const uploadFile = (file, index) => {
    uploadService
      .upload(
        file,
        (progressEvent) => {
          // Handle upload progress if needed
        },
        0,
        true
      )
      .then((text) => {
        const decryptedResponse = text && decodeBase64Json(text);
        const response = JSON.parse(decryptedResponse);

        if (response.message === "success") {
          const fileType = file.type;
          let mediaTypeId = 0;
          let thumbnailUrl = "";
          let mediaUrl = "";

          if (fileType.startsWith("image")) {
            mediaTypeId = 2;
            thumbnailUrl = response.thumbnail_path;
            mediaUrl = response.path;
          }
          if (fileType.startsWith("video")) {
            mediaTypeId = 1;
            thumbnailUrl = response.path;
            mediaUrl = response.path;
          }

          const newFile = {
            thumbnail_url: thumbnailUrl,
            mediafile: mediaUrl,
            media_type_id: mediaTypeId,
            file_type: response.file_type,
            file_size: response.file_size,
            file_name: file.name,
            id: `${Date.now()}-${index}`, // Temporary ID for tracking
          };

          setUploadedFiles((prev) => [...prev, newFile]);

          setUploadingCount((prev) => {
            const newCount = prev - 1;
            if (newCount === 0) {
              setDisableCreateButton(false);
              setShowFileUpload(false);
            }
            return newCount;
          });

          if (uploadOnServer === false) {
            setNewGalleryUploaded((prev) => [...prev, newFile]);
            if (briefImage) {
              setShowGalleryUpload(null);
            } else {
              setShowConfirmInfModal(true);
              setTab(null);
            }
          }
        } else {
          setError(true);
          setErrorMessage(response.message);
          setUploadingCount((prev) => prev - 1);
        }
      })
      .catch((err) => {
        setUploadingCount((prev) => prev - 1);
        addToast({
          error: true,
          date: new Date(),
          msg:
            typeof err === "string" ? err.replace(/<[^>]*>/g, "") : err.message,
        });
      });
  };

  const removeUploadedFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const submitBtnClick = () => {
    if (uploadedFiles.length === 0) {
      setError(true);
      addToast({
        error: true,
        date: new Date(),
        msg: "Please upload at least one file.",
      });
    } else {
      if (uploadOnServer === true) {
        saveGallery();
      }
    }
  };

  const saveGallery = async () => {
    let successCount = 0;
    let errorCount = 0;
    
    for (const file of uploadedFiles) {
      try {
        const payload = {
          mediafile: file.mediafile,
          media_type_id: file.media_type_id,
          file_type: file.file_type,
          file_size: file.file_size,
        };
        
        await galleryService.create(payload);
        successCount++;
      } catch (err) {
        console.error('Error uploading file:', err);
        errorCount++;
      }
    }
    
    setTab("main");
    if (successCount > 0) {
      addToast({ error: false, date: new Date(), msg: `${successCount} file(s) uploaded successfully` });
    }
    if (errorCount > 0) {
      addToast({ error: true, date: new Date(), msg: `${errorCount} file(s) failed to upload` });
    }
  };

  const addToast = (data) => {
    const newToastList = [...toastList];
    if (data.msg) {
      if (data.msg[data.msg.length - 1] === ".") {
        data.msg = data.msg.slice(0, -1);
      }
    }
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        createAsset.current.click();
      }
    };
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [uploadedFiles]);
  console.log("🚀 ~ GalleryAdd ~ uploadedFiles:", uploadedFiles);

  const cancelAllUploads = () => {
    setUploadedFiles([]);
    setError(false);
    setShowFileUpload(true);
  };

  return (
    <Modal addClass="galleryImgUpload" close={close}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white rounded-xl shadow-lg max-w-4xl mx-auto overflow-hidden"
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="text-xl font-semibold text-gray-800">File Upload</h3>
          <p className="text-sm text-gray-500 mt-1">
            Upload and manage your media files
          </p>
        </div>

        <div className="p-6 bg-gray-50/30">
          <div className="assetsAdd__form-inner">
            <div className="assetsAdd__form-file">
              {/* Display uploaded files grid */}
              {uploadedFiles?.length > 0 &&
                showFileUpload === false &&
                uploadOnServer === true && (
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-gray-700">
                        Selected Files ({uploadedFiles?.length})
                      </h4>
                      <button
                        onClick={cancelAllUploads}
                        className="text-sm text-red-500 hover:text-red-700 font-medium px-3 py-1 rounded-md hover:bg-red-50 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="relative group">
                          <div className="aspect-square bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs group-hover:shadow-md transition-all duration-200">
                            {/* Preview Image/Video */}
                            <div className="previewImageGallery w-full h-full flex items-center justify-center relative">
                              <div
                                className="previewImageGalleryDelete cursor-pointer absolute top-2 right-2 z-10 bg-white/90 backdrop-blur-sm rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-sm hover:bg-white hover:shadow"
                                onClick={() => removeUploadedFile(file.id)}
                              >
                                {trashGalleryIcon}
                              </div>

                              {file.media_type_id === 2 && (
                                <div className="image-box w-full h-full flex items-center justify-center">
                                  <img
                                    className="gallerypreviewassets__media w-full h-full object-cover"
                                    src={
                                      process.env.PUBLIC_URL +
                                      file.thumbnail_url
                                    }
                                    alt=""
                                  />
                                </div>
                              )}
                              {file.media_type_id === 1 && (
                                <GalleryVideo
                                  setShowActionIcon={setShowActionIcon}
                                  {...{
                                    media_url: file.mediafile,
                                    mediaType: file.media_type_id,
                                    status: "visible",
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          {/* File name */}
                          <h6 className="text-xs text-gray-600 mt-2 px-1 truncate text-center">
                            {file.file_name}
                          </h6>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Upload area - Enhanced drag & drop style */}
              <div
                className={
                  "border-2 border-dashed border-gray-300 rounded-xl bg-white/50 transition-all duration-200 " +
                  (error && uploadedFiles.length === 0
                    ? "border-red-300 bg-red-50/30"
                    : showFileUpload
                    ? "hover:border-blue-400 hover:bg-blue-50/30"
                    : "hidden") +
                  " p-8 text-center"
                }
              >
                <div className="assetsAdd__form-upload-icon flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                    {plusIcon}
                  </div>
                </div>
                <h5 className="font-medium text-gray-700 mb-2">Upload media</h5>
                <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                  .GIF, .PNG, .JPG, .MP4, .MOV only with a maximum file size of
                  10MB
                </p>

                <button className="button  bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl hover:from-emerald-600 hover:to-teal-600 focus:ring-4 focus:ring-emerald-200 focus:ring-offset-2 transition-all text-white font-medium py-2.5 px-5 duration-200 shadow-xs hover:shadow-md relative overflow-hidden">
                  Select Files
                  <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif, video/mp4, video/quicktime"
                    ref={fileRef}
                    onChange={previewImage}
                    multiple
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </button>

                {/* Upload progress indicator */}
                {uploadingCount > 0 && (
                  <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span>Uploading {uploadingCount} file(s)...</span>
                  </div>
                )}

                {/* Error messages */}
                {error && uploadedFiles.length === 0 && errorMessage === "" && (
                  <div className="mt-4 text-sm text-red-500 bg-red-50/50 py-2 px-3 rounded-lg border border-red-200">
                    Please upload at least one file
                  </div>
                )}
                {error && errorMessage !== "" && (
                  <div className="mt-4 text-sm text-red-500 bg-red-50/50 py-2 px-3 rounded-lg border border-red-200">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons - Enhanced styling */}
            {uploadOnServer === true && (
              <div className="flex flex-col sm:flex-row gap-3 justify-end items-center mt-6 pt-5 border-t border-gray-100">
                <button
                  className="w-full sm:w-auto order-2 sm:order-1 button bg-white text-gray-700 hover:bg-gray-50 font-medium py-3 px-6 rounded-lg transition-colors duration-200 border border-gray-300 hover:border-gray-400 shadow-xs"
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  ref={createAsset}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 focus:ring-4 focus:ring-emerald-200 focus:ring-offset-2 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:scale-105 transform flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-xs"
                  onClick={submitBtnClick}
                  disabled={disableCreateButton || uploadedFiles.length === 0}
                >
                  Upload{" "}
                  {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </Modal>
  );
}
